/**
 * SmsModal — replaces $scope.smsModal (templates/sms_modal.html).
 * Shows pending customers marked for SMS (text_night_before=true).
 */

import { Button, Checkbox, Text } from '@mantine/core';
import { useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '@/env';
import { ModalFrame } from './ModalFrame';
import type { RunsheetLine } from '@/types';
import { notifications } from '@mantine/notifications';

// The whole label row is the tap target, so the label fills the row and carries the
// 50px minimum height.
const ROW_STYLES = {
  body: { alignItems: 'center' },
  labelWrapper: { flex: 1 },
  label: { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 50 },
} as const;

interface Props {
  opened: boolean;
  smslist: RunsheetLine[];
  onClose: () => void;
}

export function SmsModal({ opened, smslist, onClose }: Props) {
  const [checked, setChecked] = useState<Set<number>>(
    () => new Set(smslist.map(i => i.customerid)),
  );
  const [sending, setSending] = useState(false);

  const toggle = (id: number) =>
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSend = async () => {
    const data = smslist
      .filter(i => checked.has(i.customerid))
      .map(i => ({ customerid: i.customerid, mobile: i.ph_mob }));

    if (data.length === 0) return;
    setSending(true);
    try {
      const { data: res } = await axios.post<{ success: boolean }>(`${BASE_URL}sms`, data);
      if (res.success) {
        notifications.show({ color: 'green', message: `Sent ${data.length} SMS to customers` });
        onClose();
      } else {
        notifications.show({ color: 'red', message: 'Error sending SMS' });
      }
    } catch {
      notifications.show({ color: 'red', message: 'Error sending SMS' });
    } finally {
      setSending(false);
    }
  };

  const allSelected = smslist.length > 0 && checked.size === smslist.length;

  const toggleAll = () =>
    setChecked(allSelected ? new Set() : new Set(smslist.map(i => i.customerid)));

  return (
    <ModalFrame
      opened={opened}
      onClose={onClose}
      title="SMS Clients"
      size="lg"
      actions={
        <>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button color="green" loading={sending} onClick={handleSend}>
            Send SMS ({checked.size})
          </Button>
        </>
      }
    >
      {/* The rows set Checkbox md, under the theme's lg. The 50px label row is the tap
          target, and the box only marks it. */}
      <Checkbox
        size="md"
        fw={600}
        styles={ROW_STYLES}
        checked={allSelected}
        indeterminate={checked.size > 0 && !allSelected}
        onChange={toggleAll}
        label="Select / Deselect All"
      />
      {smslist.map(item => (
        <Checkbox
          key={item.customerid}
          size="md"
          styles={ROW_STYLES}
          style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}
          checked={checked.has(item.customerid)}
          onChange={() => toggle(item.customerid)}
          label={
            <>
              <Text span display="block">
                {item.namewithid}
              </Text>
              <Text span display="block" fz="sm" c="dimmed">
                {item.deliveryaddress} · {item.ph_mob}
              </Text>
            </>
          }
        />
      ))}
    </ModalFrame>
  );
}
